import axios from 'axios';

import { CONFIG } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong!')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

// ----------------------------------------------------------------------

export const dataFetcher = async (args) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  notifications: '/api/notifications',
  auth: {
    me: '/api/staff/getstaff',
    getUser: '/api/authenticate/getuser',
    login: '/api/authenticate/login',
    register: '/api/authenticate/register',
    updateUser: '/api/authenticate/updateuser',
    deleteUserProfileImage: '/api/authenticate/deleteUserProfileImage',
    confirmEmail: '/api/authenticate/confirmEmail',
    confirmInvitation: (token) => `/api/authenticate/confirm-invitation?token=${token}`,
    verifyUpdateNewUserToken: '/api/authenticate/verifyUpdateNewUserToken',
    resendConfirmationEmail: '/api/authenticate/resendConfirmationEmail',
    refreshToken: '/api/authenticate/refresh-token',
    changePassword: '/api/authenticate/changepassword',
    resetPassword: '/api/authenticate/resetpassword',
    passwordRecovery: '/api/authenticate/passwordrecovery',
    newPassword: '/api/authenticate/newpassword',
    updatePassword: '/api/authenticate/forgetpassword',
    roles: '/api/authenticate/getroles',

    getCompanyDetails: (id) => `/api/authenticate/getcompanydetail/${id}`,
    createCompany: '/api/authenticate/createcompany',
    updateCompanyDetails: '/api/authenticate/updatecompanydetail',
    deleteCompany: (id) => `/api/authenticate/deletecompany/${id}`,

    getCompanySubscription: '/api/authenticate/getcompanysubscription',
    getCompanyPayments: '/api/authenticate/getcompanypayments',
    updateActivePage: '/api/authenticate/updateactivepage',
    updateJobListing: '/api/authenticate/updatejoblisting',
    get2FAKey: '/api/authenticate/get2fakey',
    enable2FA: '/api/authenticate/enable2fa',
    disable2FA: '/api/authenticate/disable2fa',
    get2FACodeByEmail: '/api/authenticate/get2facodebyemail',

    getMemberships: '/api/authenticate/getMemberships',
    switchMembership: (membershipId) => `/api/authenticate/switch-membership/${membershipId}`,
    deleteMembership: (membershipId) => `/api/authenticate/delete-membership/${membershipId}`,

    getOffice: '/api/authenticate/getoffice',
    getOffices: '/api/authenticate/getoffices',
    addOffice: '/api/authenticate/addoffice',
    updateOffice: '/api/authenticate/updateoffice',
    deleteOffice: '/api/authenticate/deleteoffice',

    getDepartment: '/api/authenticate/getdepartment',
    getDepartments: '/api/authenticate/getdepartments',
    addDepartment: '/api/authenticate/adddepartment',
    updateDepartment: '/api/authenticate/updatedepartment',
    deleteDepartment: '/api/authenticate/deletedepartment',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  common: {
    getHonorifics: '/api/common/gethonorifics',
    getCountries: '/api/common/getcountries',
    getCurrencies: '/api/common/getcurrencies',
    getDesignations: '/api/common/getdesignations',
    getAuditProcedureTypes: '/api/common/getAuditProcedureTypes',
    getEffectiveness: '/api/common/geteffectiveness',

    // Chart of Account
    getChartOfAccountCategories: '/api/common/chartofaccountcategories',
    getChartOfAccountsByLevels: '/api/common/chartofaccountsbylevels',
    getAllChartOfAccounts: '/api/common/allchartofaccounts',
    getChartOfAccounts: '/api/common/chartofaccounts',
    addChartOfAccount: '/api/common/addchartofaccount',
    updateChartOfAccount: '/api/common/updatechartofaccount',
    deleteChartOfAccount: '/api/common/deletechartofaccount',
    getChartOfAccountsWithAuditMappingByLevels:
      '/api/common/chartofaccountswithauditmappingbylevels',
    getChartOfAccountsWithAuditMappingHierarchy:
      '/api/common/chartofaccountswithauditmappinghierarchy',
    updateChartOfAccountWithAuditMapping: '/api/common/updatechartofaccountwithauditmapping',

    // Audit Procedures
    getAuditProcedureCategories: '/api/common/getAuditProcedureCategories',
    getAuditProcedureExplorerCategories: '/api/common/getAuditProcedureExplorerCategories',
    getAuditProcedureExplorerProcedures: '/api/common/getAuditProcedureExplorerProcedures',
    getAuditProcedureExplorerDetails: '/api/common/getAuditProcedureExplorerDetails',
    addAuditProcedureCategory: '/api/common/addAuditProcedureCategory',
    updateAuditProcedureCategory: '/api/common/updateAuditProcedureCategory',
    deleteAuditProcedureCategory: '/api/common/deleteAuditProcedureCategory',
    addAuditProcedure: '/api/common/addAuditProcedure',
    updateAuditProcedure: '/api/common/updateAuditProcedure',
    deleteAuditProcedure: '/api/common/deleteAuditProcedure',
  },
  staff: {
    getStaffs: '/api/staff/getstaffs',
    getStaff: '/api/staff/getstaff',
    addStaff: '/api/staff/addstaff',
    updateStaff: '/api/staff/updatestaff',
    activateStaff: '/api/staff/activatestaff',
    deactivateStaff: '/api/staff/deactivatestaff',
    deleteStaff: (membershipId) => `/api/staff/deletestaff/${membershipId}`,
    resendInvitation: (membershipId) => `/api/staff/resendInvitation/${membershipId}`,
  },
  client: {
    getClientHistory: (clientId) => `/api/client/getclienthistory/${clientId}`,
    getClientServices: '/api/client/getclientservices',
    getServices: '/api/client/getServices',
    getEntityTypes: '/api/client/getentitytypes',
    getShareTypes: '/api/client/getsharetypes',
    getPositionTypes: '/api/client/getpositiontypes',
    getBankAccountTypes: '/api/client/getbankaccounttypes',
    getImportantDocumentTypes: '/api/client/getdocumenttypes/CIMPORTDOCS',
    getConfidentialInformationDocumentTypes: '/api/client/getdocumenttypes/CCONFIDOCS',
    getCustomDocumentTypes: '/api/client/getdocumenttypes/CCUSTDOCS',
    getRiskFactorTypes: '/api/client/getriskfactortypes',
    getRiskLevelTypes: '/api/client/getriskleveltypes',
    getNatureTypes: '/api/client/getnaturetypes',
    getConclusionTypes: '/api/client/getconclusiontypes',

    getClient: '/api/client/getclient',
    getClients: '/api/client/getclients',
    addClient: '/api/client/addclient',
    updateClient: '/api/client/updateclient',
    activateClient: '/api/client/activateclient',
    deactivateClient: '/api/client/deactivateclient',
    deleteClient: '/api/client/deleteclient',

    getClientDetail: (clientId) => `/api/client/getclientdetail/${clientId}`,
    updateClientDetail: '/api/client/updateclientdetail',

    getContact: '/api/client/getclientcontact',
    getContacts: '/api/client/getclientcontacts',
    addContact: '/api/client/addclientcontact',
    updateContact: '/api/client/updateclientcontact',
    deactivateContact: '/api/client/deactivateclientcontact',

    getNote: '/api/client/getclientnote',
    getNotes: '/api/client/getclientnotes',
    addNote: '/api/client/addclientnote',
    updateNote: '/api/client/updateclientnote',
    deactivateNote: '/api/client/deactivateclientnote',

    getScreening: '/api/client/getclientscreening',
    getScreenings: '/api/client/getclientscreenings',
    addScreening: '/api/client/addclientscreening',
    updateScreening: '/api/client/updateclientscreening',
    deactivateScreening: '/api/client/deactivateclientscreening',
    getScreeningDocumentTypes: '/api/client/getdocumenttypes/CKYCDOCS',

    getClientDueDiligenceByClientId: '/api/client/getclientduediligencebyclientid',
    addClientDueDiligence: '/api/client/addclientduediligence',
    updateClientDueDiligence: '/api/client/updateclientduediligence',
    getClientDueDiligenceDocumentTypes: '/api/client/getdocumenttypes/CDDDOCS',
    addCustomRiskFactor: '/api/client/addcustomriskfactor',
    updateCustomRiskFactor: '/api/client/updatecustomriskfactor',
    deactivateCustomRiskFactor: '/api/client/deactivatecustomriskfactor',

    getShareholder: '/api/client/getclientshareholder',
    getShareholders: '/api/client/getclientshareholders',
    addShareholder: '/api/client/addshareholder',
    updateShareholder: '/api/client/updateshareholder',
    deactivateShareholder: '/api/client/deactivateshareholder',
    getShareholderDocumentTypes: '/api/client/getdocumenttypes/CSHDOCS',

    getBeneficialOwner: '/api/client/getclientultimatebeneficialowner',
    getBeneficialOwners: '/api/client/getclientultimatebeneficialowners',
    addBeneficialOwner: '/api/client/addultimatebeneficialowner',
    updateBeneficialOwner: '/api/client/updateultimatebeneficialowner',
    deactivateBeneficialOwner: '/api/client/deactivateultimatebeneficialowner',
    getBeneficialOwnerDocumentTypes: '/api/client/getdocumenttypes/CUBODOCS',

    getDirector: '/api/client/getclientdirectorsnexecutive',
    getDirectors: '/api/client/getclientdirectorsnexecutives',
    addDirector: '/api/client/adddirectorsnexecutive',
    updateDirector: '/api/client/updatedirectorsnexecutive',
    deactivateDirector: '/api/client/deactivatedirectorsnexecutive',
    getDirectorDocumentTypes: '/api/client/getdocumenttypes/CDNECDOCS',

    getCustomer: '/api/client/getclientcustomer',
    getCustomers: '/api/client/getclientcustomers',
    addCustomer: '/api/client/addcustomer',
    updateCustomer: '/api/client/updatecustomer',
    deactivateCustomer: '/api/client/deactivatecustomer',

    getSupplier: '/api/client/getclientsupplier',
    getSuppliers: '/api/client/getclientsuppliers',
    addSupplier: '/api/client/addsupplier',
    updateSupplier: '/api/client/updatesupplier',
    deactivateSupplier: '/api/client/deactivatesupplier',

    getBankAccount: '/api/client/getclientbankaccount',
    getBankAccounts: '/api/client/getclientbankaccounts',
    addBankAccount: '/api/client/addbankaccount',
    updateBankAccount: '/api/client/updatebankaccount',
    deactivateBankAccount: '/api/client/deactivatebankaccount',

    getLoanAgreement: '/api/client/getclientloanagreement',
    getLoanAgreements: '/api/client/getclientloanagreements',
    addLoanAgreement: '/api/client/addloanagreement',
    updateLoanAgreement: '/api/client/updateloanagreement',
    deactivateLoanAgreement: '/api/client/deactivateloanagreement',

    getRelatedParty: '/api/client/getclientrelatedparty',
    getRelatedParties: '/api/client/getclientrelatedparties',
    addRelatedParty: '/api/client/addrelatedparty',
    updateRelatedParty: '/api/client/updaterelatedparty',
    deactivateRelatedParty: '/api/client/deactivaterelatedparty',

    getDocument: '/api/client/getclientdocument',
    getImportantDocuments: '/api/client/getclientimportantdocuments',
    getConfidentialInformationDocuments: '/api/client/getclientconfidentialinformationdocuments',
    getCustomDocuments: '/api/client/getclientcustomdocuments',
    getDocumentsByType: '/api/client/getdocumentsbytype',
    addDocument: '/api/client/adddocument',
    updateDocument: '/api/client/updatedocument',
    deactivateDocument: '/api/client/deactivatedocument',
  },

  job: {
    getJobTypes: '/api/job/getjobtypes',
    getTemplates: '/api/job/gettemplates',
    getReportTemplates: '/api/job/getreporttemplates',
    getJobTemplates: '/api/job/gettemplates',
    getTeamMembers: (teamType) => `/api/job/getteammembers/${teamType}`,
    jobTeamMembers: '/api/job/jobteammembers',
    getJobHistory: (jobId) => `/api/job/getjobhistory/${jobId}`,
    getJob: '/api/job/getjob',
    getJobs: '/api/job/getjobs',
    getArchivedJobs: '/api/job/getarchivedjobs',
    archiveJob: '/api/job/archivejob',
    unarchiveJob: '/api/job/unarchivejob',
    addJob: '/api/job/addjob',
    updateJob: '/api/job/updatejob',
    exportJob: '/api/job/exportjob',
    importJob: '/api/job/importjob',
    importJobTemplate: '/api/job/importjobtemplate',
    cloneJob: '/api/job/clonejob',
    cloneJobTemplate: '/api/job/clonejobtemplate',
    changeJobStatus: '/api/job/changejobstatus',
    getJobTrialBalanceAdjustmentJournalTypes: '/api/job/getjobtrialbalanceadjustmentjournaltypes',
    getReviewPointsTypes: '/api/job/GetReviewPointsTypes',
    getReviewPointRecordTypes: '/api/job/GetReviewPointRecordTypes',
    getReviewPointPriorities: '/api/job/GetReviewPointPriorities',
    getReviewPointStatuses: '/api/job/GetReviewPointStatuses',
    getReviewPointConclusions: '/api/job/GetReviewPointConclusions',
    getWpReferenceTypes: '/api/job/getWpReferenceTypes',
    getJobLeadScheduleTypes: '/api/job/getJobLeadScheduleTypes',
    getJobTemplateDetailSectionTypes: '/api/job/getJobTemplateDetailSectionTypes',
    getJobTemplateDetailItemTypes: '/api/job/getJobTemplateDetailItemTypes',
    getJobAssertionTypes: '/api/job/getJobAssertionTypes',
    getFsliMappings: '/api/job/getfslimappings',
    getLevelTypes: '/api/job/getJobLevelTypes',
    getJobLevels: '/api/job/getJobLevels',
    getJobPreliminaryAnalyticalProcedureTypes: '/api/job/getJobPreliminaryAnalyticalProcedureTypes',
    getJobPreliminaryAnalyticalProcedureConclusions:
      '/api/job/getJobPreliminaryAnalyticalProcedureConclusions',
    getJobAuditMaterialityBenchmarks: '/api/job/getJobAuditMaterialityBenchmarks',
    getJobDesignEffectiveness: '/api/job/getJobDesignEffectiveness',

    // Job Template Details
    getJobTemplateDetails: '/api/job/getjobtemplatedetails',
    getJobTemplateDetail: '/api/job/addjobtemplatedetail',
    addJobTemplateDetail: '/api/job/addjobtemplatedetail',
    updateJobTemplateDetail: '/api/job/updatejobtemplatedetail',
    updateJobTemplateDetailSecurity: '/api/job/updatejobtemplatedetailsecurity',
    activateJobTemplateDetail: (id) => `/api/job/activatejobtemplatedetail/${id}`,
    deactivateJobTemplateDetail: (id) => `/api/job/deactivatejobtemplatedetail/${id}`,

    // Job Template Detail Sections
    getJobTemplateDetailSections: '/api/job/getjobtemplatedetailsections',
    getJobTemplateDetailSection: '/api/job/getjobtemplatedetailsection',
    addJobTemplateDetailSection: '/api/job/addjobtemplatedetailsection',
    updateJobTemplateDetailSection: '/api/job/updatejobtemplatedetailsection',
    activatejobtemplatedetailsection: '/api/job/activatejobtemplatedetailsection',
    deactivatejobtemplatedetailsection: '/api/job/deactivatejobtemplatedetailsection',

    // Job Template Detail Section Items
    getJobTemplateDetailSectionItems: '/api/job/getjobtemplatedetailsectionitems',
    getJobTemplateDetailSectionItem: '/api/job/getjobtemplatedetailsectionitem',
    addJobTemplateDetailSectionItem: '/api/job/addjobtemplatedetailsectionitem',
    updateJobTemplateDetailSectionItem: '/api/job/updatejobtemplatedetailsectionitem',
    deleteJobTemplateDetailSectionItem: '/api/job/deletejobtemplatedetailsectionitem',

    // Job Template Detail Sections Item Comments
    getJobTemplateDetailSectionItemComments: '/api/job/getjobtemplatedetailsectionitemcomments',
    addJobTemplateDetailSectionItemComment: '/api/job/addjobtemplatedetailsectionitemcomment',
    updateJobTemplateDetailSectionItemComment: '/api/job/updatejobtemplatedetailsectionitemcomment',
    deleteJobTemplateDetailSectionItemComment: '/api/job/deletejobtemplatedetailsectionitemcomment',

    // Job Template Detail Sections Item Attachments
    getJobTemplateDetailSectionItemAttachments:
      '/api/job/getjobtemplatedetailsectionitemattachments',
    addJobTemplateDetailSectionItemAttachment: '/api/job/addjobtemplatedetailsectionitemattachment',
    updateJobTemplateDetailSectionItemAttachment:
      '/api/job/updatejobtemplatedetailsectionitemattachment',
    deleteJobTemplateDetailSectionItemAttachment:
      '/api/job/deletejobtemplatedetailsectionitemattachment',

    getJobTemplateDetailSectionItemAttachmentComments:
      '/api/job/getjobtemplatedetailsectionitemattachmentcomments',
    addJobTemplateDetailSectionItemAttachmentComment:
      '/api/job/addjobtemplatedetailsectionitemattachmentcomment',
    updateJobTemplateDetailSectionItemAttachmentComment:
      '/api/job/updatejobtemplatedetailsectionitemattachmentcomment',
    deleteJobTemplateDetailSectionItemAttachmentComment:
      '/api/job/deletejobtemplatedetailsectionitemattachmentcomment',

    // Job Template Detail Sections Item Review Points
    getJobTemplateDetailSectionItemReviewPoints:
      '/api/job/getjobtemplatedetailsectionitemreviewpoints',
    addJobTemplateDetailSectionItemReviewPoint:
      '/api/job/addjobtemplatedetailsectionitemreviewpoint',
    updateJobTemplateDetailSectionItemReviewPoint:
      '/api/job/updatejobtemplatedetailsectionitemreviewpoint',
    deleteJobTemplateDetailSectionItemReviewPoint:
      '/api/job/deletejobtemplatedetailsectionitemreviewpoint',

    getJobTemplateDetailSectionItemReviewPointComments:
      '/api/job/getjobtemplatedetailsectionitemreviewpointcomments',
    addJobTemplateDetailSectionItemReviewPointComment:
      '/api/job/addjobtemplatedetailsectionitemreviewpointcomment',
    updateJobTemplateDetailSectionItemReviewPointComment:
      '/api/job/updatejobtemplatedetailsectionitemreviewpointcomment',
    deleteJobTemplateDetailSectionItemReviewPointComment:
      '/api/job/deletejobtemplatedetailsectionitemreviewpointcomment',

    getJobTemplateDetailSectionItemReviewPointAttachments:
      '/api/job/getjobtemplatedetailsectionitemreviewpointattachments',
    addJobTemplateDetailSectionItemReviewPointAttachment:
      '/api/job/addjobtemplatedetailsectionitemreviewpointattachment',
    updateJobTemplateDetailSectionItemReviewPointAttachment:
      '/api/job/updatejobtemplatedetailsectionitemreviewpointattachment',
    deleteJobTemplateDetailSectionItemReviewPointAttachment:
      '/api/job/deletejobtemplatedetailsectionitemreviewpointattachment',

    // Independence Management
    getJobIndependenceManagements: '/api/job/getjobindependencemanagements',
    getJobIndependenceManagement: '/api/job/getJobIndependenceManagement',
    addJobIndependenceManagement: '/api/job/addJobIndependenceManagement',
    updateJobIndependenceManagement: '/api/job/updateJobIndependenceManagement',
    deleteJobIndependenceManagement: '/api/job/deleteJobIndependenceManagement',

    getJobIndependenceManagementCategories: '/api/job/getJobIndependenceManagementCategories',
    getJobIndependenceManagementSafeGuards: '/api/job/getJobIndependenceManagementSafeGuards',
    addJobIndependenceManagementSafeGuard: '/api/job/addJobIndependenceManagementSafeGuard',
    updateJobIndependenceManagementSafeGuard: '/api/job/updateJobIndependenceManagementSafeGuard',
    deleteJobIndependenceManagementSafeGuard: '/api/job/deleteJobIndependenceManagementSafeGuard',

    // Risk Assessments
    getJobRiskAssessmentBySectionItemId: '/api/job/getJobRiskAssessmentBySectionItemId',
    addJobRiskAssessment: '/api/job/addjobriskassessment',
    updateJobRiskAssessment: '/api/job/updatejobriskassessment',
    deleteJobRiskAssessment: '/api/job/deletejobriskassessment',

    getJobRiskAssessmentAssertions: '/api/job/getjobriskassessmentassertions',
    addJobRiskAssessmentAssertion: '/api/job/addjobriskassessmentassertion',
    updateJobRiskAssessmentAssertion: '/api/job/updatejobriskassessmentassertion',
    deleteJobRiskAssessmentAssertion: '/api/job/deletejobriskassessmentassertion',

    getJobFraudRiskAssessmentAssertions: '/api/job/getjobfraudriskassessmentassertions',
    addJobFraudRiskAssessmentAssertion: '/api/job/addjobfraudriskassessmentassertion',
    updateJobFraudRiskAssessmentAssertion: '/api/job/updatejobfraudriskassessmentassertion',
    deleteJobFraudRiskAssessmentAssertion: '/api/job/deletejobfraudriskassessmentassertion',

    getJobRiskCategories: '/api/job/getjobriskcategories',
    addJobRiskCategory: '/api/job/addjobriskcategory',
    updateJobRiskCategory: '/api/job/updatejobriskcategory',
    deleteJobRiskCategory: '/api/job/deletejobriskcategory',

    getJobRiskIdentified: '/api/job/getjobriskidentified',
    getJobRiskIdentifiedByCategory: '/api/job/getjobriskidentifiedbycategory',
    addJobRiskIdentified: '/api/job/addjobriskidentified',
    updateJobRiskIdentified: '/api/job/updatejobriskidentified',
    deleteJobRiskIdentified: '/api/job/deletejobriskidentified',

    // Preliminary Analytical Procedures
    getJobPreliminaryAnalyticalProcedures: '/api/job/getjobpreliminaryanalyticalprocedures',
    addJobPreliminaryAnalyticalProcedureComment:
      '/api/job/addjobpreliminaryanalyticalprocedurecomment',

    addJobPreliminaryAnalyticalProcedureAttachment:
      '/api/job/addJobPreliminaryAnalyticalProcedureAttachment',
    deleteJobPreliminaryAnalyticalProcedureAttachment:
      '/api/job/deleteJobPreliminaryAnalyticalProcedureAttachment',
    addJobPreliminaryAnalyticalProcedureAttachmentComment:
      '/api/job/addJobPreliminaryAnalyticalProcedureAttachmentComment',
    updateJobPreliminaryAnalyticalProcedureAttachmentComment:
      '/api/job/updateJobPreliminaryAnalyticalProcedureAttachmentComment',
    deleteJobPreliminaryAnalyticalProcedureAttachmentComment:
      '/api/job/deleteJobPreliminaryAnalyticalProcedureAttachmentComment',

    addJobPreliminaryAnalyticalProcedureReviewPoint:
      '/api/job/addJobPreliminaryAnalyticalProcedureReviewPoint',
    updateJobPreliminaryAnalyticalProcedureReviewPoint:
      '/api/job/updateJobPreliminaryAnalyticalProcedureReviewPoint',
    deleteJobPreliminaryAnalyticalProcedureReviewPoint:
      '/api/job/deleteJobPreliminaryAnalyticalProcedureReviewPoint',
    addJobPreliminaryAnalyticalProcedureReviewPointComment:
      '/api/job/addJobPreliminaryAnalyticalProcedureReviewPointComment',
    updateJobPreliminaryAnalyticalProcedureReviewPointComment:
      '/api/job/updateJobPreliminaryAnalyticalProcedureReviewPointComment',
    deleteJobPreliminaryAnalyticalProcedureReviewPointComment:
      '/api/job/deleteJobPreliminaryAnalyticalProcedureReviewPointComment',
    addJobPreliminaryAnalyticalProcedureReviewPointAttachment:
      '/api/job/addJobPreliminaryAnalyticalProcedureReviewPointAttachment',
    updateJobPreliminaryAnalyticalProcedureReviewPointAttachment:
      '/api/job/updateJobPreliminaryAnalyticalProcedureReviewPointAttachment',
    deleteJobPreliminaryAnalyticalProcedureReviewPointAttachment:
      '/api/job/deleteJobPreliminaryAnalyticalProcedureReviewPointAttachment',

    // Control Assessment
    getJobControlAssessmentBySectionItemId: '/api/job/getJobControlAssessmentBySectionItemId',
    addJobControlAssessment: '/api/job/addjobcontrolassessment',
    updateJobControlAssessment: '/api/job/updatejobcontrolassessment',
    deleteJobControlAssessment: '/api/job/deletejobcontrolassessment',

    // Control Objectives
    getJobControlObjectives: '/api/job/getjobcontrolobjectives',
    addJobControlObjective: '/api/job/addjobcontrolobjective',
    updateJobControlObjective: '/api/job/updatejobcontrolobjective',
    deleteJobControlObjective: '/api/job/deletejobcontrolobjective',

    // Risks to Control
    getJobRisksToControl: '/api/job/getjobriskstocontrol',
    addJobRiskToControl: '/api/job/addjobriskstocontrol',
    updateJobRiskToControl: '/api/job/updatejobriskstocontrol',
    deleteJobRiskToControl: '/api/job/deletejobriskstocontrol',

    // Internal Controls
    getJobInternalControls: '/api/job/getjobinternalcontrols',
    addJobInternalControl: '/api/job/addjobinternalcontrol',
    updateJobInternalControl: '/api/job/updatejobinternalcontrol',
    deleteJobInternalControl: '/api/job/deletejobinternalcontrol',

    // Audit Materiality
    getJobAuditMaterialities: '/api/job/getJobAuditMaterialities',
    addJobAuditMateriality: '/api/job/addjobauditmateriality',

    // Design Evaluation
    getJobDesignEvaluationCriteria: '/api/job/getjobdesignevaluationcriterias',
    addJobDesignEvaluationCriteria: '/api/job/addjobdesignevaluationcriteria',
    updateJobDesignEvaluationCriteria: '/api/job/updatejobdesignevaluationcriteria',
    deleteJobDesignEvaluationCriteria: '/api/job/deletejobdesignevaluationcriteria',

    getJobDesignEvaluationCriteriaDetails: '/api/job/getjobdesignevaluationcriteriadetails',
    addJobDesignEvaluationCriteriaDetail: '/api/job/addjobdesignevaluationcriteriadetail',
    updateJobDesignEvaluationCriteriaDetail: '/api/job/updatejobdesignevaluationcriteriadetail',
    deleteJobDesignEvaluationCriteriaDetail: '/api/job/deletejobdesignevaluationcriteriadetail',

    // Lead Schedule
    getJobLeadSchedules: '/api/job/getJobLeadSchedules',
    addJobLeadScheduleComment: '/api/job/addJobLeadScheduleComment',

    addJobLeadScheduleAttachment: '/api/job/addJobLeadScheduleAttachment',
    updateJobLeadScheduleAttachment: '/api/job/updateJobLeadScheduleAttachment',
    deleteJobLeadScheduleAttachment: '/api/job/deleteJobLeadScheduleAttachment',

    addJobLeadScheduleAttachmentComment: '/api/job/addJobLeadScheduleAttachmentComment',
    updateJobLeadScheduleAttachmentComment: '/api/job/updateJobLeadScheduleAttachmentComment',
    deleteJobLeadScheduleAttachmentComment: '/api/job/deleteJobLeadScheduleAttachmentComment',

    addJobLeadScheduleReviewPoint: '/api/job/addJobLeadScheduleReviewPoint',
    updateJobLeadScheduleReviewPoint: '/api/job/updateJobLeadScheduleReviewPoint',
    deleteJobLeadScheduleReviewPoint: '/api/job/deleteJobLeadScheduleReviewPoint',

    addJobLeadScheduleReviewPointComment: '/api/job/addJobLeadScheduleReviewPointComment',
    updateJobLeadScheduleReviewPointComment: '/api/job/updateJobLeadScheduleReviewPointComment',
    deleteJobLeadScheduleReviewPointComment: '/api/job/deleteJobLeadScheduleReviewPointComment',

    addJobLeadScheduleReviewPointAttachment: '/api/job/addJobLeadScheduleReviewPointAttachment',
    updateJobLeadScheduleReviewPointAttachment:
      '/api/job/updateJobLeadScheduleReviewPointAttachment',
    deleteJobLeadScheduleReviewPointAttachment:
      '/api/job/deleteJobLeadScheduleReviewPointAttachment',

    // Risk Assessment And Audit Plan Details
    getJobRiskAssessmentAndAuditPlanDetails: '/api/job/getJobRiskAssessmentAndAuditPlanDetails',
    updateJobRiskAssessmentAndAuditPlanDetail: '/api/job/updateJobRiskAssessmentAndAuditPlanDetail',
    updateSelectedJobRiskAssessmentAndAuditPlanAuditProcedures:
      '/api/job/updateSelectedJobRiskAssessmentAndAuditPlanAuditProcedures',

    // Core Assurance Cycle Procedures
    coreAssuranceCycleProcedures: '/api/job/coreAssuranceCycleProcedures',
    coreAssuranceCycleConclusion: '/api/job/coreAssuranceCycleConclusion',

    // Job Audit Procedures
    getJobAuditProcedureCategories: '/api/job/getJobAuditProcedureCategories',
    getJobAuditProcedureExplorerCategories: '/api/job/GetJobAuditProcedureExplorerCategories',
    getJobAuditProcedureExplorerProcedures: '/api/job/GetJobAuditProcedureExplorerProcedures',
    getJobAuditProcedureExplorerDetails: '/api/job/GetJobAuditProcedureExplorerDetails',
    getSelectedJobAuditProcedureCategoriesByType:
      '/api/job/GetSelectedJobAuditProcedureCategoriesByType',
    addJobAuditProcedureCategory: '/api/job/addJobAuditProcedureCategory',
    updateJobAuditProcedureCategory: '/api/job/updateJobAuditProcedureCategory',
    deleteJobAuditProcedureCategory: '/api/job/deleteJobAuditProcedureCategory',
    addJobAuditProcedure: '/api/job/addJobAuditProcedure',
    updateJobAuditProcedure: '/api/job/updateJobAuditProcedure',
    deleteJobAuditProcedure: '/api/job/deleteJobAuditProcedure',

    // Trial Balance
    getTrialBalance: '/api/job/gettrialbalance',
    addTrialBalance: '/api/job/addtrialbalance',
    getTrialBalanceDetails: '/api/job/gettrialbalancedetails',
    uploadTrialBalanceDetail: '/api/job/uploadtrialbalancedetail',
    addTrialBalanceDetail: '/api/job/addtrialbalancedetail',
    updateTrialBalanceDetail: '/api/job/updatetrialbalancedetail',
    deleteTrialBalanceDetail: '/api/job/deletetrialbalancedetails',
    downloadTrialBalanceTemplate: '/api/job/downloadtrialbalancetemplate',

    // Job Chart of Accounts
    getTrialBalanceChartOfAccountCategories: '/api/job/trialbalancechartofaccountcategories',
    getTrialBalanceChartOfAccounts: '/api/job/trialbalancechartofaccounts',
    getTrialBalanceChartOfAccountsByLevels: '/api/job/trialbalancechartofaccountsbylevels',
    getAllTrialBalanceChartOfAccounts: '/api/job/alltrialbalancechartofaccounts',
    addTrialBalanceChartOfAccount: '/api/job/addtrialbalancechartofaccount',
    updateTrialBalanceChartOfAccount: '/api/job/updatetrialbalancechartofaccount',
    deleteTrialBalanceChartOfAccount: '/api/job/deletetrialbalancechartofaccount',

    getTrialBalanceChartOfAccountsWithAuditMapping:
      '/api/job/trialbalancechartofaccountswithauditmapping',
    getTrialBalanceChartOfAccountsWithAuditMappingByLevels:
      '/api/job/trialbalancechartofaccountswithauditmappingbylevels',
    getAllTrialBalanceChartOfAccountsWithAuditMapping:
      '/api/job/alltrialbalancechartofaccountswithauditmapping',
    getTrialBalanceChartOfAccountsWithAuditMappingHierarchy:
      '/api/job/GetTrialBalanceChartOfAccountsWithAuditMappingHierarchy',
    updateJobChartOfAccountWithAuditMapping:
      '/api/job/updateTrialBalancechartofaccountwithauditmapping',

    trialBalance: {
      getTrialBalance: '/api/job/gettrialbalance',
      addTrialBalance: '/api/job/addtrialbalance',
      getTrialBalanceDetails: '/api/job/gettrialbalancedetails',
      uploadTrialBalanceDetail: '/api/job/uploadtrialbalancedetail',
      addTrialBalanceDetail: '/api/job/addtrialbalancedetail',
      updateTrialBalanceDetail: '/api/job/updatetrialbalancedetail',
      deleteTrialBalanceDetail: '/api/job/deletetrialbalancedetails',
      downloadTrialBalanceTemplate: '/api/job/downloadtrialbalancetemplate',

      getTrialBalanceChartOfAccountCategories: '/api/job/trialbalancechartofaccountcategories',
      getTrialBalanceChartOfAccounts: '/api/job/trialbalancechartofaccounts',
      getTrialBalanceChartOfAccountsByLevels: '/api/job/trialbalancechartofaccountsbylevels',
      getAllTrialBalanceChartOfAccounts: '/api/job/alltrialbalancechartofaccounts',

      getTrialBalanceChartOfAccountsWithAuditMapping:
        '/api/job/trialbalancechartofaccountswithauditmapping',
      getTrialBalanceChartOfAccountsWithAuditMappingByLevels:
        '/api/job/trialbalancechartofaccountswithauditmappingbylevels',
      getAllTrialBalanceChartOfAccountsWithAuditMapping:
        '/api/job/alltrialbalancechartofaccountswithauditmapping',
    },

    adjustmentJournal: {
      getTrialBalanceAdjustmentJournal: '/api/job/gettrialbalanceadjustmentjournal',
      getTrialBalanceAdjustmentJournals: '/api/job/gettrialbalanceadjustmentjournals',
      addTrialBalanceAdjustmentJournal: '/api/job/addtrialbalanceadjustmentjournal',
      updateTrialBalanceAdjustmentJournal: '/api/job/updatetrialbalanceadjustmentjournal',
      deleteTrialBalanceAdjustmentJournal: '/api/job/deletetrialbalanceadjustmentjournal',
    },
  },

  task: {
    engagements: '/api/task/engagements',
    stages: '/api/task/engagement-stages',
    tasks: '/api/task/engagement-tasks',
    jobTasks: '/api/task/jobTasks',
    comments: '/api/task/engagement-task-comments',
    attachments: '/api/task/engagement-task-attachments',
    teams: '/api/task/teams',
    userStages: 'api/task/user-stages',
    userBoard: 'api/task/user-board',
  },
};
